import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HobbyEmptyState from '../../components/common/HobbyEmptyState';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ADD_TV_SHOW,
  DELETE_TV_SHOW,
  GET_TV_SHOW,
  GET_TV_SHOWS,
  SEARCH_TV_SHOWS,
  UPDATE_TV_SHOW,
} from './operations';
import TVShowsForm from './TVShowsForm';
import TVShowAddSearch from './TVShowAddSearch';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import DefaultMediaFallback from '../../components/common/DefaultMediaFallback';
import ModuleListSkeleton from '../../components/common/ModuleListSkeleton';
import DetailActionMenu from '../../components/common/DetailActionMenu';
import RatingReadout from '../../components/common/RatingReadout';
import { GET_MY_SUBSCRIPTION_STATUS } from '../../views/account/operations';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { canCreateEntries } from '../../utils/subscriptionLimits';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { shareHighlightStrings } from '../../content/shareHighlights';
import { buildShareHighlight, openShareHighlight } from '../shareHighlights/shareHighlight';

const TV_SHOWS_PAGE_SIZE = 20;

const defaultShow = {
  overview: '',
  posterUrl: '',
  firstAirYear: '',
  genres: [],
  cast: [],
  creator: '',
  network: '',
  status: 'Returning',
  userStatus: 'Want to watch',
  favorite: false,
  rating: '',
  review: '',
  spoiler: false,
  notes: '',
  collection: 'My Shows',
  whereWatched: '',
  seasons: [],
};

/**
 * Performs the split csv operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function splitCsv(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

/**
 * Normalizes seasons.
 *
 * @param {Array<*>} seasons - seasons value.
 * @returns {*} The derived operation result.
 */
function cleanSeasons(seasons = []) {
  return seasons.map((season) => ({
    seasonNumber: Number(season.seasonNumber),
    episodeCount: Number(season.episodeCount || season.episodes?.length || 0),
    episodes: (season.episodes || []).map((episode) => ({
      id: episode.id,
      seasonNumber: Number(episode.seasonNumber),
      episodeNumber: Number(episode.episodeNumber),
      title: episode.title || '',
      overview: episode.overview || '',
      airDate: episode.airDate || '',
      runtime: episode.runtime || '',
      watched: Boolean(episode.watched),
      watchedDate: episode.watchedDate || '',
    })),
  }));
}

/**
 * Normalizes show.
 *
 * @param {*} show - show value.
 * @returns {*} The derived operation result.
 */
function parseShow(show) {
  return {
    ...defaultShow,
    ...show,
    genres: Array.isArray(show.genres) ? show.genres : splitCsv(show.genres),
    cast: Array.isArray(show.cast) ? show.cast : splitCsv(show.cast),
    collection: show.collection || defaultShow.collection,
    userStatus: show.userStatus || show.status || defaultShow.userStatus,
    favorite: Boolean(show.favorite),
    seasons: cleanSeasons(show.seasons),
  };
}

/**
 * Performs the show from recommendation operation.
 *
 * @param {*} recommendation - recommendation value.
 * @returns {*} The derived operation result.
 */
function showFromRecommendation(recommendation) {
  const payload = recommendation?.actionPayload || {};
  return {
    tvdbId: payload.tvdbId || recommendation?.externalId || '',
    title: payload.title || recommendation?.title || '',
    overview: payload.overview || recommendation?.body || '',
    posterUrl: payload.posterUrl || recommendation?.media?.url || '',
    firstAirYear: payload.firstAirYear || '',
    genres: Array.isArray(payload.genres) ? payload.genres : [],
    cast: Array.isArray(payload.cast) ? payload.cast : [],
    creator: payload.creator || '',
    network: payload.network || '',
    status: payload.productionStatus || defaultShow.status,
    userStatus: 'Want to watch',
    favorite: false,
    rating: '',
    review: '',
    spoiler: false,
    notes: '',
    collection: defaultShow.collection,
    whereWatched: '',
    seasons: cleanSeasons(payload.seasons || []),
  };
}

/**
 * Performs the unique values operation.
 *
 * @param {Array<*>} items - items collection.
 * @param {*} getter - getter value.
 * @returns {*} The derived operation result.
 */
function uniqueValues(items, getter) {
  return Array.from(new Set(items.flatMap(getter).filter(Boolean))).sort();
}

/**
 * Performs the progress for operation.
 *
 * @param {*} show - show value.
 * @returns {*} The derived operation result.
 */
function progressFor(show) {
  const episodes = show.seasons.flatMap((season) => season.episodes);
  const watched = episodes.filter((episode) => episode.watched);
  const nextEpisode = episodes.find((episode) => !episode.watched);
  return {
    total: episodes.length,
    watched: watched.length,
    percent: episodes.length ? Math.round((watched.length / episodes.length) * 100) : 0,
    nextEpisode,
  };
}

/**
 * Performs the episode label operation.
 *
 * @param {*} episode - episode value.
 * @returns {*} The derived operation result.
 */
function episodeLabel(episode) {
  if (!episode) return 'No episode data';
  return `S${episode.seasonNumber} E${episode.episodeNumber}${episode.title ? ` - ${episode.title}` : ''}`;
}

/** Owns the TV library, search, episode progress, detail routing, filters, and mutations. */
export default function TVShowsModule({
  isAdding,
  setIsAdding,
  theme,
  onClose,
  onRegisterCloseGuard,
  onHeaderActionsChange,
  onHeaderTitleChange,
  modulePath = '',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const targetItemId = searchParams.get('itemId');
  const openedTargetRef = useRef(null);
  const openedRecommendationRef = useRef(null);
  const [importedShow, setImportedShow] = useState(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [editingShow, setEditingShow] = useState(null);
  const [showPendingDelete, setShowPendingDelete] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCollection, setActiveCollection] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [activeGenre, setActiveGenre] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const routeParts = useMemo(
    () => String(modulePath || '').split('/').map((part) => decodeURIComponent(part)).filter(Boolean),
    [modulePath]
  );
  const isCreateRoute = routeParts[0] === 'new';
  const routeShowId = routeParts[0] && routeParts[0] !== 'new' ? routeParts[0] : '';
  const isEditRoute = Boolean(routeShowId && routeParts[1] === 'edit');
  const isAddFlow = isAdding || isCreateRoute;
  const subscriptionRefetch = [{ query: GET_MY_SUBSCRIPTION_STATUS }];

  const { loading, error, data, fetchMore } = useQuery(GET_TV_SHOWS, {
    variables: { first: TV_SHOWS_PAGE_SIZE, after: null },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const { data: subscriptionData } = useQuery(GET_MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
  });
  const [searchTVShows, { data: searchData, loading: isSearching, error: searchError }] = useLazyQuery(SEARCH_TV_SHOWS);
  const [loadShowDetail] = useLazyQuery(GET_TV_SHOW, { fetchPolicy: 'network-only' });
  const parsedShows = useMemo(() => (data?.getTVShowsListPage?.nodes || []).map(parseShow), [data]);
  const tvShowsPageInfo = data?.getTVShowsListPage?.pageInfo || {};
  const subscription = subscriptionData?.getMySubscriptionStatus;

  const collections = useMemo(() => uniqueValues(parsedShows, (show) => [show.collection]), [parsedShows]);
  const statuses = useMemo(() => uniqueValues(parsedShows, (show) => [show.userStatus]), [parsedShows]);
  const genres = useMemo(() => uniqueValues(parsedShows, (show) => show.genres), [parsedShows]);

  const filteredShows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return parsedShows.filter((show) => {
      const searchable = [
        show.title,
        show.overview,
        show.firstAirYear,
        show.creator,
        show.network,
        show.collection,
        show.whereWatched,
        show.userStatus,
        show.genres.join(' '),
        show.cast.join(' '),
      ].join(' ').toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!activeCollection || show.collection === activeCollection) &&
        (!activeStatus || show.userStatus === activeStatus) &&
        (!activeGenre || show.genres.includes(activeGenre)) &&
        (!favoritesOnly || show.favorite)
      );
    });
  }, [activeCollection, activeGenre, activeStatus, favoritesOnly, parsedShows, search]);

  const [addTVShow, { loading: isSaving }] = useMutation(ADD_TV_SHOW, {
    refetchQueries: [{ query: GET_TV_SHOWS, variables: { first: TV_SHOWS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setImportedShow(null);
      setManualEntry(false);
      setHasFormChanges(false);
      onClose();
    },
  });

  const [updateTVShow, { loading: isUpdating }] = useMutation(UPDATE_TV_SHOW, {
    refetchQueries: [{ query: GET_TV_SHOWS, variables: { first: TV_SHOWS_PAGE_SIZE, after: null } }],
    onCompleted: (result) => {
      const updated = parseShow(result.updateTVShow);
      setEditingShow(null);
      setHasFormChanges(false);
      setSelectedShow(updated);
    },
  });

  const [deleteTVShow, { loading: isDeleting }] = useMutation(DELETE_TV_SHOW, {
    refetchQueries: [{ query: GET_TV_SHOWS, variables: { first: TV_SHOWS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setShowPendingDelete(null);
      setSelectedShow(null);
      navigate('/hobby/tvshows');
    },
  });

  /**
   * Normalizes input.
   *
   * @param {*} formData - form data value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const cleanInput = (formData) => ({
    title: formData.title.trim(),
    overview: formData.overview.trim(),
    posterUrl: formData.posterUrl.trim(),
    firstAirYear: formData.firstAirYear.trim(),
    genres: splitCsv(formData.genres),
    cast: splitCsv(formData.cast),
    creator: formData.creator.trim(),
    network: formData.network.trim(),
    status: formData.status,
    userStatus: formData.userStatus,
    favorite: Boolean(formData.favorite),
    rating: formData.rating,
    review: formData.review.trim(),
    spoiler: Boolean(formData.spoiler),
    notes: '',
    collection: formData.collection.trim() || defaultShow.collection,
    whereWatched: formData.whereWatched.trim(),
    seasons: cleanSeasons(formData.seasons),
  });

  /**
   * Handles save.
   *
   * @param {*} formData - form data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (formData) => {
    const input = cleanInput(formData);
    try {
      if (formData.id) {
        await updateTVShow({ variables: { id: formData.id, input } });
        navigate(`/hobby/tvshows/${encodeURIComponent(formData.id)}`);
        return;
      }
      await addTVShow({ variables: { input } });
      navigate('/hobby/tvshows');
    } catch (saveError) {
      if (isLimitReachedError(saveError)) {
        setShowPremiumUpsell(true);
        return;
      }
      window.alert(getPublicErrorMessage(saveError));
    }
  };

  const closeForm = useCallback(() => {
    const editingShowId = editingShow?.id;
    setImportedShow(null);
    setManualEntry(false);
    setEditingShow(null);
    setHasFormChanges(false);
    navigate(editingShowId ? `/hobby/tvshows/${encodeURIComponent(editingShowId)}` : '/hobby/tvshows');
    onClose();
  }, [editingShow?.id, navigate, onClose]);

  const handleCancelForm = useCallback(({ confirmDiscard } = {}) => {
    if (confirmDiscard && hasFormChanges) {
      const shouldDiscard = window.confirm('Discard your unsaved TV show changes?');
      if (!shouldDiscard) return;
    }
    closeForm();
  }, [closeForm, hasFormChanges]);

  const handleSearch = useCallback((query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    searchTVShows({ variables: { query: trimmedQuery } });
  }, [searchTVShows]);

  /**
   * Handles use show.
   *
   * @param {*} show - show value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleUseShow = (show) => {
    if (!canStartCreateFlow()) return;
    setImportedShow({
      title: show.title,
      overview: show.overview || '',
      posterUrl: show.posterUrl || '',
      firstAirYear: show.firstAirYear || '',
      genres: show.genres || [],
      cast: show.cast || [],
      creator: show.creator || '',
      network: show.network || '',
      status: show.status || 'Returning',
      userStatus: 'Want to watch',
      favorite: false,
      rating: '',
      review: '',
      spoiler: false,
      notes: '',
      collection: 'My Shows',
      whereWatched: '',
      seasons: cleanSeasons(show.seasons),
    });
    setManualEntry(false);
    setIsAdding(true);
    navigate('/hobby/tvshows/new');
  };

  /**
   * Handles start add.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartAdd = () => {
    if (!canStartCreateFlow()) return;
    setImportedShow(null);
    setManualEntry(false);
    setIsAdding(true);
    navigate('/hobby/tvshows/new');
  };

  /**
   * Determines whether start create flow.
   *
   * @returns {boolean} Whether the requested condition is satisfied.
   */
  const canStartCreateFlow = () => {
    if (canCreateEntries(subscription, 'tvshows')) return true;
    setShowPremiumUpsell(true);
    return false;
  };

  useEffect(() => {
    if (isAddFlow || editingShow) {
      onHeaderActionsChange?.(null);
      return () => onHeaderActionsChange?.(null);
    }

    if (selectedShow) {
      onHeaderActionsChange?.(
        <DetailActionMenu
          label="TV show actions"
          primaryAction={{ label: 'Edit', onSelect: () => handleEditShow(selectedShow) }}
          actions={[
            { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('tvshows', selectedShow)) },
            { label: mediaStrings.tvshows.deleteAction, variant: 'danger', onSelect: () => setShowPendingDelete(selectedShow) },
          ]}
        />
      );
      return () => onHeaderActionsChange?.(null);
    }

    onHeaderActionsChange?.(
      <div className="recipe-actions">
        <button className="btn-primary" type="button" onClick={handleStartAdd}>Add show</button>
      </div>
    );
    return () => onHeaderActionsChange?.(null);
  }, [editingShow, isAddFlow, onHeaderActionsChange, selectedShow, subscription]);

  useEffect(() => {
    if (isEditRoute || editingShow) {
      onHeaderTitleChange?.(mediaStrings.tvshows.form.editTitle);
    } else if (isCreateRoute || isAdding) {
      onHeaderTitleChange?.('Add show');
    } else if (routeShowId || selectedShow) {
      onHeaderTitleChange?.(selectedShow?.title || 'TV show');
    } else {
      onHeaderTitleChange?.('');
    }

    return () => onHeaderTitleChange?.('');
  }, [editingShow, isAdding, isCreateRoute, isEditRoute, onHeaderTitleChange, routeShowId, selectedShow]);

  /**
   * Renders premium upsell.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderPremiumUpsell = () => showPremiumUpsell && (
    <PremiumUpsellModal
      hobbyId="tvshows"
      subscription={subscription}
      onClose={() => setShowPremiumUpsell(false)}
      onOpenAccount={() => {
        setShowPremiumUpsell(false);
        window.location.assign('/account');
      }}
    />
  );

  /**
   * Handles confirm delete.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleConfirmDelete = async () => {
    if (!showPendingDelete) return;
    await deleteTVShow({ variables: { id: showPendingDelete.id } });
  };

  /**
   * Handles load more.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMore = async () => {
    if (!tvShowsPageInfo.hasNextPage || !tvShowsPageInfo.endCursor) return;
    await fetchMore({
      variables: {
        first: TV_SHOWS_PAGE_SIZE,
        after: tvShowsPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getTVShowsListPage) return previousResult;
        return {
          getTVShowsListPage: {
            ...fetchMoreResult.getTVShowsListPage,
            nodes: [
              ...(previousResult.getTVShowsListPage?.nodes || []),
              ...(fetchMoreResult.getTVShowsListPage.nodes || []),
            ],
          },
        };
      },
    });
  };

  const fetchShowDetail = useCallback(async (show) => {
    const result = await loadShowDetail({ variables: { id: show.id } });
    return parseShow(result.data?.getTVShow || show);
  }, [loadShowDetail]);

  /**
   * Handles select show.
   *
   * @param {*} show - show value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSelectShow = async (show) => {
    setSelectedShow(show);
    navigate(`/hobby/tvshows/${encodeURIComponent(show.id)}`);
  };

  useEffect(() => {
    if (isCreateRoute) {
      setSelectedShow(null);
      setEditingShow(null);
      setIsAdding(true);
      const recommendation = location.state?.homeRecommendation;
      const recommendationKey = recommendation?.id || `${recommendation?.provider || ''}:${recommendation?.externalId || ''}:${recommendation?.title || ''}`;
      if (recommendation?.actionType === 'add_tv_to_watchlist' && openedRecommendationRef.current !== recommendationKey) {
        openedRecommendationRef.current = recommendationKey;
        if (!canStartCreateFlow()) {
          navigate('/hobby/tvshows/new', { replace: true, state: null });
          return;
        }
        setImportedShow(showFromRecommendation(recommendation));
        setManualEntry(false);
        navigate('/hobby/tvshows/new', { replace: true, state: null });
      }
      return;
    }

    if (!routeShowId) {
      setSelectedShow(null);
      setEditingShow(null);
      setImportedShow(null);
      setManualEntry(false);
      setIsAdding(false);
      return;
    }

    setIsAdding(false);
    const show = parsedShows.find((item) => item.id === routeShowId) || { id: routeShowId };
    fetchShowDetail(show)
      .then((detail) => {
        if (isEditRoute) {
          setSelectedShow(null);
          setEditingShow(detail);
          return;
        }
        setEditingShow(null);
        setSelectedShow(detail);
      })
      .catch(() => navigate('/hobby/tvshows', { replace: true }));
  }, [fetchShowDetail, isCreateRoute, isEditRoute, location.state, navigate, parsedShows, routeShowId, setIsAdding, subscription]);

  /**
   * Handles edit show.
   *
   * @param {*} show - show value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleEditShow = async (show) => {
    navigate(`/hobby/tvshows/${encodeURIComponent(show.id)}/edit`);
    setEditingShow(await fetchShowDetail(show));
  };

  useEffect(() => {
    if (!targetItemId || openedTargetRef.current === targetItemId) return;
    openedTargetRef.current = targetItemId;
    navigate(`/hobby/tvshows/${encodeURIComponent(targetItemId)}`, { replace: true });
  }, [navigate, targetItemId]);

  useEffect(() => {
    if (isAddFlow || editingShow) {
      onRegisterCloseGuard?.(() => handleCancelForm({ confirmDiscard: true }));
      return () => onRegisterCloseGuard?.(null);
    }

    if (selectedShow) {
      onRegisterCloseGuard?.(() => navigate('/hobby/tvshows'));
      return () => onRegisterCloseGuard?.(null);
    }

    onRegisterCloseGuard?.(null);
    return undefined;
  }, [editingShow, handleCancelForm, isAddFlow, navigate, onRegisterCloseGuard, selectedShow]);

  if (isAddFlow && !editingShow && !importedShow && !manualEntry) {
    return (
      <>
        <TVShowAddSearch
          results={searchData?.searchTVShows || []}
          onCancel={handleCancelForm}
          onManualEntry={() => {
            if (!canStartCreateFlow()) return;
            setManualEntry(true);
          }}
          onSearch={handleSearch}
          onUseShow={handleUseShow}
          isSearching={isSearching}
          error={searchError}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (isAddFlow || editingShow) {
    return (
      <>
        <TVShowsForm
          theme={theme}
          onCancel={handleCancelForm}
          onSave={handleSave}
          isSaving={isSaving || isUpdating}
          initialData={editingShow || importedShow}
          onDirtyChange={setHasFormChanges}
          watchlists={collections}
          isEditing={Boolean(editingShow)}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (selectedShow) {
    const progress = progressFor(selectedShow);
    const heroFacts = [
      selectedShow.firstAirYear && [mediaStrings.tvshows.form.fields.firstAirYear, selectedShow.firstAirYear],
      selectedShow.network && [mediaStrings.tvshows.form.fields.network, selectedShow.network],
      selectedShow.rating && [mediaStrings.tvshows.form.fields.rating, <RatingReadout rating={selectedShow.rating} />],
    ].filter(Boolean);
    const libraryRows = [
      [mediaStrings.library.status, selectedShow.userStatus],
      [mediaStrings.tvshows.form.fields.collection, selectedShow.collection],
      [selectedShow.userStatus === 'Watched' || selectedShow.userStatus === 'Abandoned' ? mediaStrings.tvshows.form.fields.watchedOn : mediaStrings.tvshows.form.fields.watchOn, selectedShow.whereWatched],
      [mediaStrings.tvshows.upNextTitle, progress.nextEpisode ? episodeLabel(progress.nextEpisode) : mediaStrings.tvshows.caughtUp],
      [mediaStrings.tvshows.seasonsTitle, progress.total ? mediaStrings.tvshows.episodes(progress.watched, progress.total) : ''],
      [mediaStrings.library.favorites, selectedShow.favorite ? mediaStrings.library.favorites : ''],
    ];
    const detailRows = [
      [mediaStrings.tvshows.form.fields.creator, selectedShow.creator],
      [mediaStrings.tvshows.form.fields.status, selectedShow.status],
      [mediaStrings.tvshows.form.fields.genres, selectedShow.genres.join(', ')],
      [mediaStrings.tvshows.form.fields.cast, selectedShow.cast.join(', ')],
    ];

    return (
      <div className="module-wrapper tv-module">
        <div className="recipe-detail-view">
          <button type="button" className="recipe-back-link" onClick={() => navigate('/hobby/tvshows')}>← Back</button>

          {showPendingDelete && (
            <div className="modal-overlay">
              <div className="modal-content delete-confirmation">
                <p className="eyebrow">{mediaStrings.tvshows.deleteTitle}</p>
                <h2>{mediaStrings.tvshows.deletePrompt}</h2>
                <p>{mediaStrings.tvshows.deleteDetail(showPendingDelete.title)}</p>
                <div className="form-actions">
                  <button type="button" className="btn-ghost" onClick={() => setShowPendingDelete(null)}>{mediaStrings.library.cancel}</button>
                  <button type="button" className="btn-danger solid" onClick={handleConfirmDelete} disabled={isDeleting}>
                    {isDeleting ? mediaStrings.library.deleting : mediaStrings.tvshows.deleteAction}
                  </button>
                </div>
              </div>
            </div>
          )}

          <section className="recipe-detail-hero movie-detail-hero">
            {selectedShow.posterUrl ? (
              <img className="movie-detail-poster" src={selectedShow.posterUrl} alt="" />
            ) : (
              <DefaultMediaFallback className="entry-detail-photo" fallbackType="tvshows" variant="detail-square" />
            )}
            <div className="movie-detail-copy">
              <p className="eyebrow">{selectedShow.collection}</p>
              <h2>{selectedShow.title}</h2>
              <p>{[selectedShow.firstAirYear, selectedShow.network, selectedShow.creator && mediaStrings.tvshows.createdBy(selectedShow.creator)].filter(Boolean).join(' - ')}</p>
              {selectedShow.overview && <p>{selectedShow.overview}</p>}
            </div>
            {heroFacts.length > 0 && (
              <div className="recipe-detail-facts">
                {heroFacts.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="recipe-detail-grid">
            <section className="recipe-detail-panel">
              <h3>{mediaStrings.tvshows.form.sections.library}</h3>
              <dl className="movie-info-list">
                {libraryRows.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="recipe-detail-panel">
              <h3>{mediaStrings.tvshows.detailsTitle}</h3>
              <dl className="movie-info-list">
                {detailRows.filter(([, value]) => value).map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>

          <section className="recipe-detail-panel">
            <h3>{mediaStrings.tvshows.upNextTitle}</h3>
            <div className="tv-up-next">
              {progress.nextEpisode ? (
                <>
                  <span>{episodeLabel(progress.nextEpisode)}</span>
                </>
              ) : (
                <strong>{mediaStrings.tvshows.caughtUp}</strong>
              )}
            </div>
          </section>

          {selectedShow.review && (
            <section className="recipe-detail-panel">
              <h3>{selectedShow.spoiler ? mediaStrings.library.spoilers : mediaStrings.tvshows.reviewTitle}</h3>
              <p className="detail-notes">{selectedShow.review}</p>
            </section>
          )}

          <section className="recipe-detail-panel">
            <h3>{mediaStrings.tvshows.seasonsTitle}</h3>
            <div className="tv-season-list">
              {selectedShow.seasons.map((season) => (
                <div className="tv-season" key={season.seasonNumber}>
                  <h4>{mediaStrings.tvshows.seasonTitle(season.seasonNumber)}</h4>
                  <div className="tv-episode-list">
                    {season.episodes.map((episode) => (
                      <div
                        className={`tv-episode-row ${episode.watched ? 'watched' : ''}`}
                        key={episode.id}
                      >
                        <span className={`tv-episode-check ${episode.watched ? 'watched' : ''}`} aria-hidden="true">{episode.watched ? '✓' : ''}</span>
                        <span>{episodeLabel(episode)}</span>
                        <strong>{episode.watched ? mediaStrings.tvshows.watched : mediaStrings.tvshows.unwatched}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="module-wrapper tv-module movies-module">
      <div className="recipe-library-layout">
        <aside className="recipe-books-panel">
          <p className="eyebrow">{mediaStrings.library.watchlists}</p>
          <div className="recipe-book-list">
            <button type="button" className={!activeCollection ? 'active' : ''} onClick={() => setActiveCollection('')}>
              <span>{mediaStrings.tvshows.allItems}</span>
              <strong>{parsedShows.length}</strong>
            </button>
            {collections.map((collection) => (
              <button key={collection} type="button" className={activeCollection === collection ? 'active' : ''} onClick={() => setActiveCollection(collection)}>
                <span>{collection}</span>
                <strong>{parsedShows.filter((show) => show.collection === collection).length}</strong>
              </button>
            ))}
          </div>
        </aside>

        <section className="recipe-main-panel">
          <div className="recipe-filters">
            <div className="form-group search-field">
              <label>{mediaStrings.tvshows.addAction}</label>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={mediaStrings.tvshows.searchPlaceholder} />
            </div>

            <div className="filter-row movie-filter-row">
              <label>
                {mediaStrings.library.status}
                <select value={activeStatus} onChange={(event) => setActiveStatus(event.target.value)}>
                  <option value="">{mediaStrings.library.anyStatus}</option>
                  {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
              <label>
                {mediaStrings.library.genre}
                <select value={activeGenre} onChange={(event) => setActiveGenre(event.target.value)}>
                  <option value="">{mediaStrings.library.anyGenre}</option>
                  {genres.map((genre) => <option key={genre} value={genre}>{genre}</option>)}
                </select>
              </label>
              <button type="button" className={`favorite-filter ${favoritesOnly ? 'active' : ''}`} onClick={() => setFavoritesOnly((current) => !current)}>
                {mediaStrings.library.favorites}
              </button>
            </div>
          </div>

          {loading && parsedShows.length === 0 && (
            <div className="module-loading-state">
              <ModuleListSkeleton />
            </div>
          )}
          {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

          <div className="recipe-stats">
            <span>{mediaStrings.library.shown(filteredShows.length)}</span>
            <span>{mediaStrings.library.favoritesCount(parsedShows.filter((show) => show.favorite).length)}</span>
            <span>{mediaStrings.library.watchlistsCount(collections.length)}</span>
          </div>

          <div className="recipe-card-grid">
            {filteredShows.map((show) => {
              const progress = progressFor(show);
              return (
                <button key={show.id} type="button" className="recipe-card movie-card" onClick={() => handleSelectShow(show)}>
                  <div className="movie-card-title">
                    {show.posterUrl ? (
                      <img className="movie-poster-thumb" src={show.posterUrl} alt="" />
                    ) : (
                      <DefaultMediaFallback className="entry-card-photo" fallbackType="tvshows" variant="thumbnail-square" />
                    )}
                    <div>
                      <div className="movie-card-heading">
                        <h3>{show.title}</h3>
                        {show.favorite && <span className="favorite-chip">{mediaStrings.library.favorites}</span>}
                      </div>
                      <p>{[show.firstAirYear, show.creator, show.network].filter(Boolean).join(' • ') || mediaStrings.tvshows.detailsMissing}</p>
                      <p className="movie-card-detail-line">
                        {[show.userStatus, show.collection, show.whereWatched, progress.total ? mediaStrings.tvshows.episodes(progress.watched, progress.total) : ''].filter(Boolean).join(' • ') || mediaStrings.library.libraryDetailsMissing}
                      </p>
                    </div>
                  </div>
                  {show.rating && <span className="movie-rating-pill" aria-label={mediaStrings.library.ratingLabel(show.rating)}>★ {show.rating}</span>}
                </button>
              );
            })}
          </div>

          {tvShowsPageInfo.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading shows...' : 'Load more shows'}
              </button>
            </div>
          )}

          {filteredShows.length === 0 && !loading && (
            <HobbyEmptyState hobbyId="tvshows" filtered={parsedShows.length > 0}
              title={parsedShows.length === 0 ? mediaStrings.tvshows.emptyTitle : moduleCommonStrings.empty.noMatches}
              detail={parsedShows.length === 0 ? mediaStrings.tvshows.emptyFirst : moduleCommonStrings.filters.tryDifferentSearchOrFilter} />
          )}
        </section>
      </div>
      {renderPremiumUpsell()}
    </div>
  );
}
